/**
 * 商品管理 API 服务
 */

interface Product {
  id: number;
  productId: string;
  name: string;
  subtitle?: string;
  description?: string;
  category: string;
  price: number;
  originalPrice?: number;
  coverImageUrl?: string;
  imageUrls?: string[];
  purchaseUrl?: string;
  purchasePlatform?: string;
  specs?: Record<string, string>;
  features?: string[];
  sortOrder: number;
  status: number; // 0-下架, 1-上架
  createdAt: string;
  updatedAt: string;
}

interface ProductCategory {
  id: number;
  code: string;
  name: string;
  iconUrl?: string;
  description?: string;
  sortOrder: number;
  status: number;
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
}

// Android 客户端兼容格式
interface CodeResponse<T = any> {
  code: number;
  message?: string;
  data?: T;
  timestamp?: number;
}

const API_BASE = '/api';

/**
 * 商品 API 服务
 */
export const productApi = {
  /**
   * 获取商品列表
   */
  async getProducts(params?: {
    category?: string;
    page?: number;
    size?: number;
    status?: number;
  }): Promise<{ total: number; page: number; size: number; list: Product[] }> {
    const query = new URLSearchParams();
    if (params?.category) query.set('category', params.category);
    if (params?.page !== undefined) query.set('page', params.page.toString());
    if (params?.size !== undefined) query.set('page_size', params.size.toString());

    const response = await fetch(`${API_BASE}/products?${query.toString()}`);
    const result: CodeResponse<{ list: Product[]; total: number; page: number; size: number }> = await response.json();

    if (result.code === 0 && result.data) {
      return {
        total: result.data.total,
        page: result.data.page,
        size: result.data.size,
        list: result.data.list || [],
      };
    }
    throw new Error(result.message || '获取商品列表失败');
  },

  /**
   * 获取商品详情
   */
  async getProduct(productId: string): Promise<Product> {
    const response = await fetch(`${API_BASE}/products/${productId}`);
    const result: CodeResponse<Product> = await response.json();

    if (result.code === 0 && result.data) {
      return result.data;
    }
    throw new Error(result.message || '获取商品详情失败');
  },

  /**
   * 获取分类列表
   */
  async getCategories(): Promise<ProductCategory[]> {
    const response = await fetch(`${API_BASE}/categories`);
    const result: ApiResponse<ProductCategory[]> = await response.json();

    if (result.success && result.data) {
      return result.data;
    }
    return [];
  },
};

/**
 * 商品管理 API 服务（需要管理员权限）
 */
export const productAdminApi = {
  /**
   * 获取所有商品（包括下架的，用于管理后台）
   */
  async getAllProducts(
    accessToken: string,
    params?: {
      category?: string;
      page?: number;
      size?: number;
      status?: number;
    }
  ): Promise<{ total: number; page: number; size: number; list: Product[] }> {
    const query = new URLSearchParams();
    if (params?.category) query.set('category', params.category);
    if (params?.page !== undefined) query.set('page', params.page.toString());
    if (params?.size !== undefined) query.set('page_size', params.size.toString());

    const response = await fetch(`${API_BASE}/admin/products?${query.toString()}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const result: CodeResponse<{ list: Product[]; total: number; page: number; size: number }> = await response.json();

    if (result.code === 0 && result.data) {
      return {
        total: result.data.total,
        page: result.data.page,
        size: result.data.size,
        list: result.data.list || [],
      };
    }
    throw new Error(result.message || '获取商品列表失败');
  },

  /**
   * 创建商品
   */
  async createProduct(
    data: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>,
    accessToken: string
  ): Promise<Product> {
    const response = await fetch(`${API_BASE}/admin/products`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });
    const result: ApiResponse<Product> = await response.json();

    if (result.success && result.data) {
      return result.data;
    }
    throw new Error(result.message || '创建商品失败');
  },

  /**
   * 更新商品
   */
  async updateProduct(
    productId: string,
    data: Partial<Omit<Product, 'id' | 'productId' | 'createdAt' | 'updatedAt'>>,
    accessToken: string
  ): Promise<void> {
    const response = await fetch(`${API_BASE}/admin/products/${productId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });
    const result: ApiResponse = await response.json();

    if (!result.success) {
      throw new Error(result.message || '更新商品失败');
    }
  },

  /**
   * 删除商品
   */
  async deleteProduct(productId: string, accessToken: string): Promise<void> {
    const response = await fetch(`${API_BASE}/admin/products/${productId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const result: ApiResponse = await response.json();

    if (!result.success) {
      throw new Error(result.message || '删除商品失败');
    }
  },

  /**
   * 上架商品
   */
  async publishProduct(productId: string, accessToken: string): Promise<void> {
    const response = await fetch(`${API_BASE}/admin/products/${productId}/publish`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const result: ApiResponse = await response.json();

    if (!result.success) {
      throw new Error(result.message || '上架商品失败');
    }
  },

  /**
   * 下架商品
   */
  async unpublishProduct(productId: string, accessToken: string): Promise<void> {
    const response = await fetch(`${API_BASE}/admin/products/${productId}/unpublish`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const result: ApiResponse = await response.json();

    if (!result.success) {
      throw new Error(result.message || '下架商品失败');
    }
  },

  /**
   * 上传图片
   */
  async uploadImage(file: File, accessToken: string): Promise<{ url: string; filename: string; size: number; mimeType: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/upload/image`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    });
    const result: ApiResponse<{ url: string; filename: string; size: number; mimeType: string }> = await response.json();

    if (result.success && result.data) {
      return result.data;
    }
    throw new Error(result.message || '上传图片失败');
  },
};

export type { Product, ProductCategory };
